import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";
import {
  ContractOverviewResponse,
  ContractAnalyticsResponse,
} from "../types/contracts";
import {
  SupplierOverviewResponse,
  SupplierWithMostContractsResponse,
  SuppliersDirectoryApiResponse,
} from "../types/suppliers";
import { SourcingOverviewResponse, SourcingPerformanceResponse } from "../types/sourcing";

// Async thunk to fetch both contract overview and analytics in parallel
export const fetchAllContractData = createAsyncThunk<
  {
    overview: ContractOverviewResponse;
    analytics: ContractAnalyticsResponse;
  },
  void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchAllContractData",
  async (_, { rejectWithValue }) => {
    try {
      const [overviewResponse, analyticsResponse] = await Promise.all([
        axiosClient.post<ContractOverviewResponse>("/contract-overview"),
        axiosClient.post<ContractAnalyticsResponse>("/contract-analytics"),
      ]);

      return {
        overview: overviewResponse.data,
        analytics: analyticsResponse.data,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch contract data"
      );
    }
  }
);

// Async thunk to fetch all supplier data in parallel (overview, most contracts, and directory)
export const fetchAllSupplierData = createAsyncThunk<
  {
    overview: SupplierOverviewResponse;
    suppliersWithMostContracts: SupplierWithMostContractsResponse;
    suppliersDirectory: SuppliersDirectoryApiResponse;
  },
  { limit?: number; offset?: number } | void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchAllSupplierData",
  async (params, { rejectWithValue }) => {
    try {
      const limit = params && 'limit' in params ? params.limit : 10;
      const offset = params && 'offset' in params ? params.offset : 0;

      const [overviewResponse, suppliersWithMostContractsResponse, directoryResponse] = await Promise.all([
        axiosClient.post<SupplierOverviewResponse>("/supplier-overview"),
        axiosClient.post<SupplierWithMostContractsResponse>("/supplier-with-most-contracts"),
        axiosClient.post<SuppliersDirectoryApiResponse>(`/suppliers-directory?limit=${limit}&offset=${offset}`),
      ]);

      return {
        overview: overviewResponse.data,
        suppliersWithMostContracts: suppliersWithMostContractsResponse.data,
        suppliersDirectory: directoryResponse.data,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch supplier data"
      );
    }
  }
);

// Async thunk to fetch only suppliers directory with pagination (for pagination updates)
export const fetchSuppliersDirectory = createAsyncThunk<
  SuppliersDirectoryApiResponse,
  { limit: number; offset: number },
  { rejectValue: string }
>(
  "procurementDashboard/fetchSuppliersDirectory",
  async ({ limit, offset }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SuppliersDirectoryApiResponse>(
        `/suppliers-directory?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch suppliers directory"
      );
    }
  }
);

// Async thunk to fetch sourcing overview data
export const fetchSourcingOverview = createAsyncThunk<
  SourcingOverviewResponse,
  void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchSourcingOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SourcingOverviewResponse>(
        "/sourcing-overview"
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing overview data"
      );
    }
  }
);

// Async thunk to fetch sourcing performance data
export const fetchSourcingPerformance = createAsyncThunk<
  SourcingPerformanceResponse,
  void,
  { rejectValue: string }
>(
  "procurementDashboard/fetchSourcingPerformance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post<SourcingPerformanceResponse>(
        "/sourcing-performance"
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch sourcing performance data"
      );
    }
  }
);

interface ProcurementDashboardState {
  contractOverview: ContractOverviewResponse | null;
  contractAnalytics: ContractAnalyticsResponse | null;
  supplierOverview: SupplierOverviewResponse | null;
  suppliersWithMostContracts: SupplierWithMostContractsResponse | null;
  suppliersDirectory: SuppliersDirectoryApiResponse | null;
  sourcingOverview: SourcingOverviewResponse | null;
  sourcingPerformance: SourcingPerformanceResponse | null;
  contractsLoading: boolean;
  contractsError: string | null;
  suppliersLoading: boolean;
  suppliersError: string | null;
  sourcingLoading: boolean;
  sourcingError: string | null;
  sourcingPerformanceLoading: boolean;
  sourcingPerformanceError: string | null;
  lastContractsFetched: number | null;
  lastSuppliersFetched: number | null;
  lastSourcingFetched: number | null;
  lastSourcingPerformanceFetched: number | null;
}

const initialState: ProcurementDashboardState = {
  contractOverview: null,
  contractAnalytics: null,
  supplierOverview: null,
  suppliersWithMostContracts: null,
  suppliersDirectory: null,
  sourcingOverview: null,
  sourcingPerformance: null,
  contractsLoading: false,
  contractsError: null,
  suppliersLoading: false,
  suppliersError: null,
  sourcingLoading: false,
  sourcingError: null,
  sourcingPerformanceLoading: false,
  sourcingPerformanceError: null,
  lastContractsFetched: null,
  lastSuppliersFetched: null,
  lastSourcingFetched: null,
  lastSourcingPerformanceFetched: null,
};

const procurementDashboardSlice = createSlice({
  name: "procurementDashboard",
  initialState,
  reducers: {
    clearContractsError: (state) => {
      state.contractsError = null;
    },
    clearSuppliersError: (state) => {
      state.suppliersError = null;
    },
    clearSourcingError: (state) => {
      state.sourcingError = null;
    },
    clearSourcingPerformanceError: (state) => {
      state.sourcingPerformanceError = null;
    },
    clearAllErrors: (state) => {
      state.contractsError = null;
      state.suppliersError = null;
      state.sourcingError = null;
      state.sourcingPerformanceError = null;
    },
    clearContractData: (state) => {
      state.contractOverview = null;
      state.contractAnalytics = null;
      state.contractsError = null;
      state.contractsLoading = false;
      state.lastContractsFetched = null;
    },
    clearSupplierData: (state) => {
      state.supplierOverview = null;
      state.suppliersWithMostContracts = null;
      state.suppliersDirectory = null;
      state.suppliersError = null;
      state.suppliersLoading = false;
      state.lastSuppliersFetched = null;
    },
    clearSourcingData: (state) => {
      state.sourcingOverview = null;
      state.sourcingPerformance = null;
      state.sourcingError = null;
      state.sourcingLoading = false;
      state.sourcingPerformanceError = null;
      state.sourcingPerformanceLoading = false;
      state.lastSourcingFetched = null;
      state.lastSourcingPerformanceFetched = null;
    },
    clearAllData: (state) => {
      state.contractOverview = null;
      state.contractAnalytics = null;
      state.supplierOverview = null;
      state.suppliersWithMostContracts = null;
      state.suppliersDirectory = null;
      state.sourcingOverview = null;
      state.sourcingPerformance = null;
      state.contractsError = null;
      state.contractsLoading = false;
      state.suppliersError = null;
      state.suppliersLoading = false;
      state.sourcingError = null;
      state.sourcingLoading = false;
      state.sourcingPerformanceError = null;
      state.sourcingPerformanceLoading = false;
      state.lastContractsFetched = null;
      state.lastSuppliersFetched = null;
      state.lastSourcingFetched = null;
      state.lastSourcingPerformanceFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContractData.pending, (state) => {
        state.contractsLoading = true;
        state.contractsError = null;
      })
      .addCase(fetchAllContractData.fulfilled, (state, action) => {
        state.contractsLoading = false;
        state.contractOverview = action.payload.overview;
        state.contractAnalytics = action.payload.analytics;
        state.contractsError = null;
        state.lastContractsFetched = Date.now();
      })
      .addCase(fetchAllContractData.rejected, (state, action) => {
        state.contractsLoading = false;
        state.contractsError = action.payload || "Failed to fetch contract data";
      })
      .addCase(fetchAllSupplierData.pending, (state) => {
        state.suppliersLoading = true;
        state.suppliersError = null;
      })
      .addCase(fetchAllSupplierData.fulfilled, (state, action) => {
        state.suppliersLoading = false;
        state.supplierOverview = action.payload.overview;
        state.suppliersWithMostContracts = action.payload.suppliersWithMostContracts;
        state.suppliersDirectory = action.payload.suppliersDirectory;
        state.suppliersError = null;
        state.lastSuppliersFetched = Date.now();
      })
      .addCase(fetchAllSupplierData.rejected, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersError = action.payload || "Failed to fetch supplier data";
      })
      .addCase(fetchSuppliersDirectory.pending, (state) => {
        state.suppliersLoading = true;
      })
      .addCase(fetchSuppliersDirectory.fulfilled, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersDirectory = action.payload;
        state.suppliersError = null;
      })
      .addCase(fetchSuppliersDirectory.rejected, (state, action) => {
        state.suppliersLoading = false;
        state.suppliersError = action.payload || "Failed to fetch suppliers directory";
      })
      .addCase(fetchSourcingOverview.pending, (state) => {
        state.sourcingLoading = true;
        state.sourcingError = null;
      })
      .addCase(fetchSourcingOverview.fulfilled, (state, action) => {
        state.sourcingLoading = false;
        state.sourcingOverview = action.payload;
        state.sourcingError = null;
        state.lastSourcingFetched = Date.now();
      })
      .addCase(fetchSourcingOverview.rejected, (state, action) => {
        state.sourcingLoading = false;
        state.sourcingError = action.payload || "Failed to fetch sourcing overview data";
      })
      .addCase(fetchSourcingPerformance.pending, (state) => {
        state.sourcingPerformanceLoading = true;
        state.sourcingPerformanceError = null;
      })
      .addCase(fetchSourcingPerformance.fulfilled, (state, action) => {
        state.sourcingPerformanceLoading = false;
        state.sourcingPerformance = action.payload;
        state.sourcingPerformanceError = null;
        state.lastSourcingPerformanceFetched = Date.now();
      })
      .addCase(fetchSourcingPerformance.rejected, (state, action) => {
        state.sourcingPerformanceLoading = false;
        state.sourcingPerformanceError = action.payload || "Failed to fetch sourcing performance data";
      });
  },
});

export const {
  clearContractsError,
  clearSuppliersError,
  clearSourcingError,
  clearSourcingPerformanceError,
  clearAllErrors,
  clearContractData,
  clearSupplierData,
  clearSourcingData,
  clearAllData,
} = procurementDashboardSlice.actions;
export default procurementDashboardSlice.reducer;
